import React, { useEffect, useState } from 'react';
import { 
  getBaseMaagFlat, 
  getEnseignePrix, 
  setEnseignePrix,
  setEnseignePrixBatch 
} from '../services/api';
import { 
  Euro, 
  Save, 
  X,
  Search,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const PrixEnseigneModal = ({ enseigne, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [baseMaag, setBaseMaag] = useState([]);
  const [prixEnseigne, setPrixEnseigne] = useState({});
  const [search, setSearch] = useState('');
  const [modifiedCodes, setModifiedCodes] = useState(new Set());

  useEffect(() => {
    loadData();
  }, [enseigne.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [baseRes, prixRes] = await Promise.all([
        getBaseMaagFlat(),
        getEnseignePrix(enseigne.id)
      ]);
      
      setBaseMaag(baseRes.data);
      
      // Convert prix list to map
      const prixMap = {};
      prixRes.data.forEach(p => {
        prixMap[p.prestation_code] = p.prix_unitaire;
      });
      setPrixEnseigne(prixMap);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handlePrixChange = (code, value) => {
    const prix = parseFloat(value) || 0;
    setPrixEnseigne(prev => ({
      ...prev,
      [code]: prix
    }));
    setModifiedCodes(prev => new Set([...prev, code]));
  };

  const handleSaveAll = async () => {
    if (modifiedCodes.size === 0) {
      toast.info('Aucune modification à enregistrer');
      return;
    }

    setSaving(true);
    try {
      const prixToSave = Array.from(modifiedCodes).map(code => ({
        prestation_code: code,
        prix_unitaire: prixEnseigne[code] || 0
      }));

      await setEnseignePrixBatch(enseigne.id, prixToSave);
      toast.success(`${prixToSave.length} prix enregistrés pour ${enseigne.nom}`);
      setModifiedCodes(new Set());
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const filteredPrestations = baseMaag.filter(p => 
    search === '' || 
    p.designation.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase()) ||
    p.lot_nom.toLowerCase().includes(search.toLowerCase())
  );

  // Group by lot
  const groupedByLot = filteredPrestations.reduce((acc, p) => {
    if (!acc[p.lot_id]) {
      acc[p.lot_id] = {
        lot_id: p.lot_id,
        lot_nom: p.lot_nom,
        prestations: []
      };
    }
    acc[p.lot_id].prestations.push(p);
    return acc;
  }, {});

  const prestationCount = baseMaag.length;
  const prixDefiniCount = Object.values(prixEnseigne).filter(p => p > 0).length;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
          <p className="text-sm text-slate-500 mt-4">Chargement des prix...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-4">
              <Euro className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Prix {enseigne.nom}</h2>
              <p className="text-sm text-slate-500">
                {prixDefiniCount}/{prestationCount} prix définis
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Search and stats */}
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une prestation..."
                className="pl-10"
              />
            </div>
            {modifiedCodes.size > 0 && (
              <div className="flex items-center text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                {modifiedCodes.size} modification{modifiedCodes.size > 1 ? 's' : ''} non enregistrée{modifiedCodes.size > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {baseMaag.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune prestation dans la base MAAG</p>
              <p className="text-sm text-slate-400">Ajoutez d'abord des prestations dans la Base MAAG</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.values(groupedByLot).map(lot => (
                <div key={lot.lot_id} className="bg-white rounded-lg border border-slate-200">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 rounded-t-lg">
                    <h3 className="font-semibold text-sm text-slate-700">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-amber-100 rounded text-amber-700 text-xs font-bold mr-2">
                        {lot.lot_id}
                      </span>
                      {lot.lot_nom}
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {lot.prestations.map(prestation => (
                      <div key={prestation.code} className="flex items-center px-4 py-3 hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded mr-2">
                              {prestation.code}
                            </span>
                            <span className="text-sm font-medium text-slate-900 truncate">
                              {prestation.designation}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {prestation.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <span className="text-xs text-slate-400">{prestation.unite}</span>
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={prixEnseigne[prestation.code] || ''}
                              onChange={(e) => handlePrixChange(prestation.code, e.target.value)}
                              placeholder="0.00"
                              className={`w-28 h-9 px-3 pr-8 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                                modifiedCodes.has(prestation.code)
                                  ? 'border-amber-400 bg-amber-50'
                                  : 'border-slate-300'
                              }`}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                          </div>
                          {prixEnseigne[prestation.code] > 0 && !modifiedCodes.has(prestation.code) && (
                            <Check className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
          <p className="text-sm text-slate-500">
            Les prix sont automatiquement appliqués aux nouveaux devis de cette enseigne
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
            <Button 
              onClick={handleSaveAll} 
              disabled={saving || modifiedCodes.size === 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving ? (
                <>Enregistrement...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les prix
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrixEnseigneModal;
